/**
 * Push Notification Service
 * Handles service worker registration, push subscriptions, and notifications
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';

export class PushNotificationService {
  private registration: ServiceWorkerRegistration | null = null;
  private vapidPublicKey: string | null = null;

  /**
   * Initialize service worker and push notifications
   */
  async initialize(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      console.warn('[Push] Service workers not supported');
      return false;
    }

    if (!('PushManager' in window)) {
      console.warn('[Push] Push notifications not supported');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log('[Push] Service worker registered');

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Get VAPID public key from backend
      await this.fetchVapidPublicKey();

      return true;
    } catch (error) {
      console.error('[Push] Failed to initialize:', error);
      return false;
    }
  }

  /**
   * Fetch VAPID public key from backend
   */
  private async fetchVapidPublicKey(): Promise<void> {
    try {
      const response = await fetch(`${API_URL}/v1/push/vapid-public-key`);
      if (!response.ok) {
        throw new Error('Failed to fetch VAPID public key');
      }
      const data = await response.json();
      this.vapidPublicKey = data.public_key;
      console.log('[Push] VAPID public key fetched');
    } catch (error) {
      console.error('[Push] Failed to fetch VAPID key:', error);
      throw error;
    }
  }

  /**
   * Check if notifications are supported
   */
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    return Notification.permission;
  }

  /**
   * Check if user is currently subscribed
   */
  async isSubscribed(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.error('[Push] Failed to check subscription:', error);
      return false;
    }
  }

  /**
   * Request notification permission and subscribe to push
   * Uses cookie-based authentication
   */
  async requestPermissionAndSubscribe(): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn('[Push] Push notifications not supported');
      return false;
    }

    if (!this.registration) {
      await this.initialize();
    }

    if (!this.registration) {
      console.error('[Push] Service worker not registered');
      return false;
    }

    try {
      // Request permission
      const permission = await Notification.requestPermission();
      console.log('[Push] Permission status:', permission);

      if (permission !== 'granted') {
        console.log('[Push] Permission denied');
        return false;
      }

      // Subscribe to push
      if (!this.vapidPublicKey) {
        await this.fetchVapidPublicKey();
      }

      if (!this.vapidPublicKey) {
        throw new Error('VAPID public key not available');
      }

      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey) as BufferSource,
      });

      console.log('[Push] Subscribed to push notifications');

      // Send subscription to backend
      await this.sendSubscriptionToBackend(subscription);

      return true;
    } catch (error) {
      console.error('[Push] Failed to subscribe:', error);
      return false;
    }
  }

  /**
   * Unsubscribe from push notifications
   * Uses cookie-based authentication
   */
  async unsubscribe(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      if (!subscription) {
        console.log('[Push] No active subscription');
        return true;
      }

      // Notify backend first
      await this.removeSubscriptionFromBackend(subscription);

      // Unsubscribe locally
      await subscription.unsubscribe();
      console.log('[Push] Unsubscribed from push notifications');

      return true;
    } catch (error) {
      console.error('[Push] Failed to unsubscribe:', error);
      return false;
    }
  }

  /**
   * Send subscription to backend
   * Uses cookie-based authentication
   */
  private async sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    const subscriptionJson = subscription.toJSON();

    const response = await fetch(`${API_URL}/v1/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Use cookie-based auth
      body: JSON.stringify({
        endpoint: subscriptionJson.endpoint,
        keys: {
          p256dh: subscriptionJson.keys?.p256dh || '',
          auth: subscriptionJson.keys?.auth || '',
        },
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to send subscription to backend');
    }

    console.log('[Push] Subscription saved to backend');
  }

  /**
   * Remove subscription from backend
   * Uses cookie-based authentication
   */
  private async removeSubscriptionFromBackend(subscription: PushSubscription): Promise<void> {
    const subscriptionJson = subscription.toJSON();

    const response = await fetch(`${API_URL}/v1/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Use cookie-based auth
      body: JSON.stringify({
        endpoint: subscriptionJson.endpoint,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove subscription from backend');
    }

    console.log('[Push] Subscription removed from backend');
  }

  /**
   * Convert VAPID public key from base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }

  /**
   * Show a test notification (for testing purposes)
   */
  async showTestNotification(): Promise<void> {
    if (!this.registration) {
      console.error('[Push] Service worker not registered');
      return;
    }

    await this.registration.showNotification('Test Notification', {
      body: 'This is a test notification from QuarkChat',
      icon: '/icon-192.png',
      badge: '/icon-192.png',
    });
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();
